import {Component, EventEmitter, OnDestroy, OnInit} from '@angular/core';
import {TOAST_TYPE} from "../../constants/Tpiq.constants";
import {Session} from "../../services/Session";
import {takeUntil} from "rxjs/operators";
import {FileUploader, FileUploaderOptions, FileUploadModule} from "ng2-file-upload";
import {PageTitleService} from "../../services/PageTitle";
import {StateService} from "../../services/StateService";
import {Router} from "@angular/router";
import {LoadingService} from "../../services/LoadingService";
import {SwitchBoard} from "../../services/SwitchBoard";
import {ToastService} from "../../services/Toast.service";
import {TaxPlanningService} from "../../tpiq-services/tax-planning.service";
import {LogoService} from "../../services/LogoService";
import {ManageTaxStrategiesServices} from "../../tpiq-services/manage-tax-strategies.service";
import {environment} from "../../../environments/environment";
import {v4 as uuid} from 'uuid';
import {CommonModule} from "@angular/common";
import {exceedsLogoLimit, oversizedLogoMessage, readImageSize} from "./logo-size";

@Component({
  selector: 'app-manage-firm-logo',
  standalone: true,
  imports: [CommonModule, FileUploadModule],
  templateUrl: './manage-firm-logo.component.html',
  styleUrl: './manage-firm-logo.component.scss'
})
export class ManageFirmLogoComponent implements OnInit, OnDestroy {
  org: string;
  userId: string;
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  routeSub: any;
  uploader: FileUploader;
  imageUrl = '';
  hasBaseDropZoneOver: boolean;
  currentEnvironment: any;
  logoBg: any;
  displayLogo: boolean = true;

  constructor(private titleService: PageTitleService, private stateService: StateService, private _router: Router,
              private loadingService: LoadingService, private switchBoard: SwitchBoard, private toastService: ToastService,
              private taxPlanningService: TaxPlanningService, private logoService: LogoService,
              private taxStrategyServices: ManageTaxStrategiesServices) {
  }

  ngOnInit() {
    this._init();
  }

  _init() {
    this.org = Session.getOrg();
    this.userId = Session.getUser().id;
    this.currentEnvironment = environment;
    this.titleService.setPageTitle('Manage Logo');
    this.imageUrl = this.logoService.getLoginLogoUrl() || '';
    this.logoBg = {
      background: 'url("' + this.imageUrl + '") center center no-repeat',
      'background-size': 'contain'
    };

    this.uploaderUpdate();
    this.buildItemForm();
    this.switchBoard.onPageChangeListener.next({});
    this.switchBoard.onClickPrev
      .pipe(takeUntil(this.destroy$)).subscribe(title => {
      this.gotoDefaultPage();
    });

  }

  gotoDefaultPage() {
    const prevState = this.stateService.getPrevState();
    if (prevState) {
      this.stateService.pop();
      this._router.navigate([prevState.url]);
    }
  }

  ngOnDestroy() {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  uploaderUpdate() {
    const base = this;
    const docUrl: any = this.taxStrategyServices.getFirmLogoUrl(this.org, Session.getUser().id);
    this.uploader = new FileUploader({
      url: docUrl,
      headers: [
        {
          name: 'Authorization',
          value: 'Bearer ' + Session.getToken()
        }
      ]
    } as FileUploaderOptions);
    this.uploader.onAfterAddingFile = file => {
      file.withCredentials = false;
    };
  }

  buildItemForm() {
    const base = this;
    const user: any = Session.getUser();
    this.uploader.onBuildItemForm = (fileItem: any, form: any) => {
      const payload: any = {};
      payload.sourceType = "firm_logo";
      payload.sourceId = uuid();
      form.append('payload', JSON.stringify(payload));
    };
    this.uploader.onCompleteItem = (item, response, status, header) => {
      if (status === 200) {
        this.uploader.progress = 100;
        const imageData = JSON.parse(response);
        this.imageUrl = imageData.temporaryURL;
        this.displayLogo = false;
        this.logoBg = {
          background: 'url("' + this.imageUrl + '") center center no-repeat',
          'background-size': 'contain'
        };
        setTimeout(() => {
          this.displayLogo = true;
        }, 5);
        this.updateNewLogoForFirm();
        this.loadingService.triggerLoadingEvent(false);
      } else {
        this.resetUploader();
        this.toastService.pop(TOAST_TYPE.error, this.uploadErrorMessage(status, response));
        this.loadingService.triggerLoadingEvent(false);
      }
    };
  }

  // The backend returns 400 with a size message when the logo is over the limit.
  uploadErrorMessage(status: number, response: string): string {
    if (status === 400) {
      try {
        const message = JSON.parse(response)?.message;
        if (message) {
          return message;
        }
      } catch (e) {
      }
    }
    return 'Failed To Upload Logo';
  }

  resetUploader() {
    this.uploader.queue.forEach(function(item) {
      item.remove();
    });
  }

  fileDropOver($event){
    this.loadingService.triggerLoadingEvent(true);
    const items = this.uploader.getNotUploadedItems();
    Promise.all(items.map(item => readImageSize(item._file))).then(sizes => {
      const oversized = sizes.find(size => exceedsLogoLimit(size));
      if (oversized) {
        this.resetUploader();
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, oversizedLogoMessage(oversized));
        return;
      }
      this.uploader.uploadAll();
    }).catch(() => {
      this.loadingService.triggerLoadingEvent(false);
    });
  }

  fileOverBase($event){
    this.hasBaseDropZoneOver = $event;
  }

  submitLogoDetails() {
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.updateLogoDetails(Session.getOrg(), this.userId, this.imageUrl).subscribe(response => {
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(TOAST_TYPE.success, 'Logo updated successfully');
      this.taxPlanningService.setHasLogo(true);
    }, error => {
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(TOAST_TYPE.error, 'Failed to update Logo');
    });
  }

  updateNewLogoForFirm() {
    let logosData: any = this.logoService.getLogoUrls();
    if (logosData) {
      logosData['loginLogoUrl'] = this.imageUrl;
      this.logoService.setLogoUrls(logosData);
    }
  }

}
